import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, X, Star, Plus, Image, User, Camera } from 'lucide-react';
import useTheme from '../hooks/useTheme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditProfilePage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const profilePhotoInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'photos' ? 'photos' : 'profile');
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    show_age: true,
    location: '',
    bio: '',
    occupation: '',
    hobbies: '',
    religion: '',
    ethnicity: '',
    music: '',
    favorite_drinks: '',
    favorite_hospo: '',
    favorite_brands: '',
    photos: []
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/profile`, formData);
      alert('Profile updated successfully!');
      navigate('/profile');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile');
    }
  };

  // Photo management functions
  const handleAddPhotoClick = () => {
    if (formData.photos.length >= 10) {
      alert('Maximum 10 photos allowed');
      return;
    }
    fileInputRef.current?.click();
  };

  const handleProfilePhotoClick = () => {
    profilePhotoInputRef.current?.click();
  };

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const remainingSlots = 10 - formData.photos.length;
    const filesToProcess = Array.from(files).slice(0, remainingSlots);

    setUploading(true);
    
    try {
      const newPhotos = [];
      for (const file of filesToProcess) {
        const base64 = await convertToBase64(file);
        newPhotos.push(base64);
      }
      
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...newPhotos]
      }));
    } catch (error) {
      console.error('Error processing photos:', error);
      alert('Error uploading photos');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleProfilePhotoSelect = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    
    try {
      const base64 = await convertToBase64(files[0]);
      // Set as first photo (profile photo)
      setFormData(prev => ({
        ...prev,
        photos: [base64, ...prev.photos.slice(0, 9)]
      }));
    } catch (error) {
      console.error('Error processing photo:', error);
      alert('Error uploading photo');
    } finally {
      setUploading(false);
      if (profilePhotoInputRef.current) {
        profilePhotoInputRef.current.value = '';
      }
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleRemovePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSetProfilePhoto = (index) => {
    const photos = [...formData.photos];
    const [selectedPhoto] = photos.splice(index, 1);
    photos.unshift(selectedPhoto);
    setFormData(prev => ({
      ...prev,
      photos
    }));
  };

  const handleAddPhotoUrl = () => {
    if (formData.photos.length >= 10) {
      alert('Maximum 10 photos allowed');
      return;
    }
    const url = prompt('Enter photo URL:');
    if (url && url.trim()) {
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, url.trim()]
      }));
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'photos', label: 'Photos', icon: Camera }
  ];

  // Theme-aware colors
  const colors = {
    background: isDark ? '#0D0D0D' : '#FDFCFA',
    text: isDark ? '#E6D7C3' : '#1A1410',
    textMuted: isDark ? '#8A7355' : '#6D6358',
    textAccent: isDark ? '#C8A878' : '#8B6914',
    gold: isDark ? '#D7B56D' : '#8B6914',
    goldGradient: isDark 
      ? 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)' 
      : 'linear-gradient(135deg, #A57C33 0%, #8B6914 100%)',
    border: isDark ? '#A57C33' : 'rgba(139, 105, 20, 0.3)',
    inputBg: isDark ? 'rgba(26, 15, 10, 0.8)' : '#FFFFFF',
    photoBg: isDark ? '#1A0F0A' : '#F5F2ED',
    overlayBg: isDark ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.6)'
  };

  return (
    <div style={{ paddingBottom: '100px', background: colors.background, minHeight: '100vh' }}>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', background: isDark ? 'var(--bg-secondary)' : '#F5F2ED' }}>
        <button onClick={() => navigate('/profile')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textAccent, padding: '8px' }}>
          <ArrowLeft size={24} />
        </button>
        <div style={{ flex: 1 }}>
          <h1 className="page-title">Edit Profile</h1>
        </div>
        <button onClick={handleSave} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textAccent, padding: '8px' }} data-testid="save-profile-btn">
          <Save size={24} />
        </button>
      </div>

      {/* Profile Photo - Clickable to change */}
      <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 20px 0' }}>
        <div 
          onClick={handleProfilePhotoClick}
          style={{ 
            position: 'relative',
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            overflow: 'hidden',
            border: `3px solid ${colors.gold}`,
            cursor: 'pointer',
            background: colors.photoBg
          }}
        >
          {formData.photos?.[0] ? (
            <img 
              src={formData.photos[0]} 
              alt="Profile"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <div style={{ 
              width: '100%', 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              background: isDark ? 'rgba(215, 181, 109, 0.1)' : 'rgba(139, 105, 20, 0.08)'
            }}>
              <User size={48} color={colors.border} />
            </div>
          )}
          {/* Camera overlay */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: `linear-gradient(transparent, ${colors.overlayBg})`,
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Camera size={20} color={colors.gold} />
          </div>
        </div>
        <input
          ref={profilePhotoInputRef}
          type="file"
          accept="image/*"
          onChange={handleProfilePhotoSelect}
          style={{ display: 'none' }}
        />
      </div>
      <p style={{ textAlign: 'center', fontSize: '13px', color: colors.textMuted, marginTop: '8px' }}>
        Tap to change profile photo
      </p>

      {/* Tab Navigation */}
      <div style={{ 
        display: 'flex', 
        borderBottom: `1px solid ${colors.border}`,
        margin: '20px 20px 0'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex: 1,
              padding: '14px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab.id ? `3px solid ${colors.gold}` : '3px solid transparent',
              color: activeTab === tab.id ? colors.gold : colors.textMuted,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              fontSize: '15px',
              fontWeight: activeTab === tab.id ? '600' : '400',
              transition: 'all 0.3s ease'
            }}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ padding: '20px' }}>
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: colors.textAccent }}>Name</label>
              <input
                type="text"
                name="name"
                className="input-field"
                value={formData.name}
                onChange={handleChange}
                data-testid="profile-name-input"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: colors.textAccent }}>Age</label>
              <input
                type="number"
                name="age"
                className="input-field"
                value={formData.age}
                onChange={handleChange}
                data-testid="profile-age-input"
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', cursor: 'pointer', color: colors.text }}>
                <input
                  type="checkbox"
                  name="show_age"
                  checked={formData.show_age}
                  onChange={handleChange}
                  data-testid="profile-show-age-checkbox"
                />
                <span style={{ fontSize: '14px' }}>Show my age: {formData.show_age ? 'ON' : 'OFF'}</span>
              </label>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: colors.textAccent }}>Location</label>
              <input
                type="text"
                name="location"
                className="input-field"
                value={formData.location}
                onChange={handleChange}
                placeholder="City / Neighborhood"
                data-testid="profile-location-input"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: colors.textAccent }}>Bio</label>
              <textarea
                name="bio"
                className="input-field"
                value={formData.bio}
                onChange={handleChange}
                rows="4"
                placeholder="Tell us about yourself..."
                data-testid="profile-bio-input"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: colors.textAccent }}>Occupation</label>
              <input
                type="text"
                name="occupation"
                className="input-field"
                value={formData.occupation}
                onChange={handleChange}
                data-testid="profile-occupation-input"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: colors.textAccent }}>Hobbies / Interests</label>
              <input
                type="text"
                name="hobbies"
                className="input-field"
                value={formData.hobbies}
                onChange={handleChange}
                placeholder="e.g., Dancing, Photography, Travel"
                data-testid="profile-hobbies-input"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: colors.textAccent }}>Religion</label>
              <input
                type="text"
                name="religion"
                className="input-field"
                value={formData.religion}
                onChange={handleChange}
                data-testid="profile-religion-input"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: colors.textAccent }}>Race / Ethnicity</label>
              <input
                type="text"
                name="ethnicity"
                className="input-field"
                value={formData.ethnicity}
                onChange={handleChange}
                data-testid="profile-ethnicity-input"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: colors.textAccent }}>Favorite Music</label>
              <input
                type="text"
                name="music"
                className="input-field"
                value={formData.music}
                onChange={handleChange}
                placeholder="e.g., House, Techno, Latin, Hip-Hop"
                data-testid="profile-music-input"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: colors.textAccent }}>Favorite Drinks</label>
              <input
                type="text"
                name="favorite_drinks"
                className="input-field"
                value={formData.favorite_drinks}
                onChange={handleChange}
                placeholder="e.g., Espresso Martini, Dom Pérignon, Old Fashioned"
                data-testid="profile-favorite-drinks-input"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: colors.textAccent }}>Favorite Hotspots</label>
              <input
                type="text"
                name="favorite_hospo"
                className="input-field"
                value={formData.favorite_hospo}
                onChange={handleChange}
                placeholder="e.g., LIV, Faena, MILA, E11even"
                data-testid="profile-favorite-hospo-input"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: colors.textAccent }}>Favorite Brands</label>
              <input
                type="text"
                name="favorite_brands"
                className="input-field"
                value={formData.favorite_brands}
                onChange={handleChange}
                placeholder="e.g., Gucci, Louis Vuitton, Rolex"
                data-testid="profile-favorite-brands-input"
              />
            </div>

            <button onClick={handleSave} className="btn-primary" style={{ width: '100%', marginTop: '20px' }} data-testid="save-profile-footer-btn">
              Save Changes
            </button>
          </div>
        )}

        {/* Photos Tab */}
        {activeTab === 'photos' && (
          <div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h3 style={{ color: colors.gold, fontSize: '18px', margin: 0 }}>
                My Photos ({formData.photos?.length || 0}/10)
              </h3>
            </div>
            
            <p style={{ fontSize: '13px', color: colors.textMuted, marginBottom: '20px' }}>
              Your first photo is your profile picture. Tap the ⭐ to set any photo as your profile picture.
            </p>

            {/* Photo Grid */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: '12px',
              marginBottom: '24px'
            }}>
              {formData.photos?.map((photo, index) => (
                <div 
                  key={index} 
                  style={{ 
                    position: 'relative',
                    aspectRatio: '1',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: index === 0 ? `3px solid ${colors.gold}` : `1px solid ${colors.border}`
                  }}
                >
                  <img 
                    src={photo} 
                    alt={`Photo ${index + 1}`}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover' 
                    }}
                  />
                  
                  {/* Profile Photo Badge */}
                  {index === 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      left: '8px',
                      background: colors.goldGradient,
                      color: isDark ? '#2C1B13' : '#FFFFFF',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '10px',
                      fontWeight: 'bold'
                    }}>
                      PROFILE
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '8px',
                    display: 'flex',
                    gap: '6px'
                  }}>
                    {/* Set as Profile Photo */}
                    {index !== 0 && (
                      <button
                        onClick={() => handleSetProfilePhoto(index)}
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          background: isDark ? 'rgba(44, 27, 19, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                          border: `2px solid ${colors.gold}`,
                          color: colors.gold,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="Set as profile photo"
                      >
                        <Star size={18} />
                      </button>
                    )}
                    
                    {/* Remove Photo */}
                    <button
                      onClick={() => handleRemovePhoto(index)}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: 'rgba(139, 50, 50, 0.95)',
                        border: '2px solid #8B3232',
                        color: '#FFFFFF',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Delete photo"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>
              ))}
              
              {/* Add Photo Button - shows in grid if less than 10 photos */}
              {(formData.photos?.length || 0) < 10 && (
                <div 
                  onClick={handleAddPhotoClick}
                  style={{ 
                    aspectRatio: '1',
                    borderRadius: '12px',
                    border: `2px dashed ${colors.border}`,
                    background: isDark ? 'rgba(230, 215, 195, 0.05)' : 'rgba(139, 105, 20, 0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {uploading ? (
                    <div style={{ color: colors.border, fontSize: '14px' }}>Uploading...</div>
                  ) : (
                    <>
                      <Plus size={36} color={colors.border} />
                      <span style={{ color: colors.border, fontSize: '13px', marginTop: '8px' }}>Add Photo</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Add Photo Actions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={handleAddPhotoClick}
                disabled={formData.photos?.length >= 10 || uploading}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: colors.goldGradient,
                  border: 'none',
                  borderRadius: '12px',
                  color: isDark ? '#2C1B13' : '#FFFFFF',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: formData.photos?.length >= 10 ? 'not-allowed' : 'pointer',
                  opacity: formData.photos?.length >= 10 ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}
              >
                <Camera size={20} />
                {uploading ? 'Uploading...' : 'Upload from Device'}
              </button>
              
              <button
                onClick={handleAddPhotoUrl}
                disabled={formData.photos?.length >= 10}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: 'transparent',
                  border: `2px solid ${colors.border}`,
                  borderRadius: '12px',
                  color: formData.photos?.length >= 10 ? colors.textMuted : colors.gold,
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: formData.photos?.length >= 10 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}
              >
                <Image size={20} />
                Add Photo by URL
              </button>
            </div>

            {/* Hidden File Input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            <button onClick={handleSave} className="btn-primary" style={{ width: '100%', marginTop: '24px' }} data-testid="save-photos-btn">
              Save Changes
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditProfilePage;
